/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Euro, Star } from 'lucide-react';
import { initials } from '@/lib/utils-cat';
import { cn } from '@/lib/utils';
import VerifiedBadge from '@/components/VerifiedBadge';

export const StarRating = ({ rating, reviews }) => (
  <div className="flex items-center gap-1 text-xs">
    <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
    <span className="font-bold text-foreground">{rating?.toFixed(1)}</span>
    {reviews != null && <span className="text-muted-foreground">({reviews})</span>}
  </div>
);

export const DoctorCard = ({ doctor, limits, onBook, priorityAccent }) => {
  const { t } = useTranslation();
  const outOfFree = limits && limits.remaining === 0;
  return (
    <motion.div
      whileHover={{ y: -3 }}
      transition={{ type: 'spring', stiffness: 300, damping: 22 }}
      className={cn(
        'group rounded-2xl bg-card border p-5 flex flex-col gap-3 relative overflow-hidden shadow-elev-sm hover:shadow-elev-lg transition-shadow',
        priorityAccent ? 'border-accent/40' : 'border-border/60'
      )}
      data-testid={`doctor-card-${doctor.id}`}
    >
      {priorityAccent && (
        <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
      )}
      <div className="flex items-start gap-3 relative">
        <Avatar className="h-14 w-14 ring-2 ring-background shadow-elev-sm">
          {doctor.avatar_base64 && <AvatarImage src={doctor.avatar_base64} />}
          <AvatarFallback className="bg-primary-gradient text-primary-foreground font-bold">{initials(doctor.full_name)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate flex items-center gap-1" data-testid={`doctor-name-${doctor.id}`}>
            <span className="truncate">Dr. {doctor.full_name}</span>
            {doctor.albo_verified && <VerifiedBadge verified size="sm" albo={{ number: doctor.albo_number, province: doctor.albo_province }} testId={`doctor-verified-${doctor.id}`} />}
          </div>
          {doctor.specialty && <div className="text-xs text-muted-foreground truncate">{doctor.specialty}</div>}
          <div className="mt-1"><StarRating rating={doctor.rating} reviews={doctor.reviews_count} /></div>
        </div>
      </div>
      {doctor.consultation_bio && (
        <p className="text-xs text-muted-foreground line-clamp-2">{doctor.consultation_bio}</p>
      )}
      <div className="mt-auto flex items-center justify-between pt-2">
        <div className="text-primary font-bold text-lg flex items-center gap-0.5">
          <Euro className="h-4 w-4" />
          {doctor.visit_price_eur?.toFixed(2)}
        </div>
        <Button onClick={() => onBook(doctor)} size="sm" data-testid={`book-doctor-btn-${doctor.id}`}>
          {outOfFree ? t('telemedPayAndBook') : t('telemedBookFree')}
        </Button>
      </div>
    </motion.div>
  );
};
